import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// ============================================
// CIRCUIT BREAKER FOR AUTH (Jan 30, 2026)
// Prevents retry storms when Supabase auth is down
// ============================================
const AUTH_CIRCUIT_BREAKER = {
  failures: 0,
  maxFailures: 3,
  resetTimeout: 30000, // 30 seconds
  lastFailure: 0,
  isOpen: false,
};

// Check if circuit breaker is open (should stop retrying)
export function isAuthCircuitOpen(): boolean {
  if (!AUTH_CIRCUIT_BREAKER.isOpen) return false;
  
  // Check if reset timeout has passed
  if (Date.now() - AUTH_CIRCUIT_BREAKER.lastFailure > AUTH_CIRCUIT_BREAKER.resetTimeout) {
    console.log('[auth-circuit-breaker] Reset timeout passed, closing circuit');
    AUTH_CIRCUIT_BREAKER.isOpen = false;
    AUTH_CIRCUIT_BREAKER.failures = 0;
    return false;
  }
  
  return true;
}

// Record an auth failure
export function recordAuthFailure(): void {
  AUTH_CIRCUIT_BREAKER.failures++;
  AUTH_CIRCUIT_BREAKER.lastFailure = Date.now();
  
  if (AUTH_CIRCUIT_BREAKER.failures >= AUTH_CIRCUIT_BREAKER.maxFailures) {
    console.error(`[auth-circuit-breaker] Circuit OPEN after ${AUTH_CIRCUIT_BREAKER.failures} failures`);
    AUTH_CIRCUIT_BREAKER.isOpen = true;
    
    // Dispatch event for UI to handle
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('supabase:auth:unavailable', {
        detail: { 
          message: 'Authentication service unavailable',
          retryAfter: AUTH_CIRCUIT_BREAKER.resetTimeout 
        }
      }));
    }
  }
}

// Reset circuit breaker (e.g., after successful auth)
export function resetAuthCircuit(): void {
  AUTH_CIRCUIT_BREAKER.failures = 0;
  AUTH_CIRCUIT_BREAKER.isOpen = false;
  AUTH_CIRCUIT_BREAKER.lastFailure = 0;
}

// Manual retry - resets circuit and reloads
export function retryAuth(): void {
  console.log('[auth-circuit-breaker] Manual retry triggered');
  resetAuthCircuit();
  window.location.reload();
}

const baseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // Important: avoid treating provider OAuth callbacks (e.g., Slack with ?code=) as Supabase auth callbacks
    detectSessionInUrl: false,
    flowType: 'pkce',
    storage: window.localStorage
  }
});

// Sovereign/gov tenant has NO Supabase Realtime service — supabase-js otherwise
// retries wss://<host>/realtime/v1/websocket forever, flooding the console. The
// socket connects lazily (only on .channel().subscribe()), so stub channel() to an
// inert object: every existing .channel(...).on(...).subscribe() call site stays
// unchanged and simply never opens a socket. Commercial builds are untouched.
if (import.meta.env.VITE_SOVEREIGN === 'true') {
  const stubChannel: any = {
    on: () => stubChannel,
    subscribe: () => stubChannel,
    unsubscribe: async () => 'ok',
    send: async () => 'ok',
    track: async () => 'ok',
    untrack: async () => 'ok',
  };
  (baseClient as any).channel = () => stubChannel;
  (baseClient as any).removeChannel = async () => 'ok';
  (baseClient as any).removeAllChannels = async () => 'ok';
}

// Intercept auth errors to trigger circuit breaker
const originalGetSession = baseClient.auth.getSession.bind(baseClient.auth);
baseClient.auth.getSession = async function() {
  // If circuit is open, return null session instead of hammering the server
  if (isAuthCircuitOpen()) {
    console.warn('[auth-circuit-breaker] Circuit open, returning null session');
    return { data: { session: null }, error: new Error('Auth service unavailable - circuit breaker open') as any };
  }
  
  try {
    const result = await originalGetSession();
    
    // If we got a session, reset the circuit
    if (result.data.session) {
      resetAuthCircuit();
    }
    
    return result;
  } catch (error: any) {
    // Check for 503 or network errors
    if (error?.status === 503 || error?.message?.includes('503') || error?.message?.includes('network')) {
      recordAuthFailure();
    }
    throw error;
  }
};

// Wrap the functions.invoke method to attach auth headers and capture errors
const originalInvoke = baseClient.functions.invoke.bind(baseClient.functions);

baseClient.functions.invoke = async function(functionName: string, options?: any) {
  try {
    // Ensure Authorization header is always included when a session exists
    const { data: { session } } = await baseClient.auth.getSession();

    const mergedHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers || {}),
    };

    if (session?.access_token && !mergedHeaders['Authorization']) {
      mergedHeaders['Authorization'] = `Bearer ${session.access_token}`;
    }

    const result = await originalInvoke(functionName, {
      ...options,
      headers: mergedHeaders,
    });
    
    // Check for edge function errors
    if (result.error) {
      console.error(`Edge function error (${functionName}):`, result.error);
      
      // Dispatch debug event
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('app:debug:error', {
          detail: {
            message: `Edge function error: ${functionName}`,
            source: 'Supabase Functions',
            details: {
              function: functionName,
              error: result.error,
              options
            }
          }
        }));
      }
    }
    
    return result;
  } catch (error) {
    console.error(`Edge function exception (${functionName}):`, error);
    
    // Dispatch debug event
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:debug:error', {
        detail: {
          message: `Edge function exception: ${functionName}`,
          source: 'Supabase Functions',
          details: {
            function: functionName,
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            options
          }
        }
      }));
    }
    
    throw error;
  }
};

export const supabase = baseClient;

// ============================================
// READ REPLICA CLIENT (2026-05-22) — DISABLED 2026-05-30
// ============================================
// `supabaseRead` was intended to point at the Supabase read replica
// (VITE_SUPABASE_REPLICA_URL, the `-rr-…` host) for freshness-tolerant reads.
//
// DISABLED because it broke the dashboard: the replica client forwarded the
// session into a SECOND client pointed at the replica host, which issued
// `/auth/v1/user` (and other auth) calls to the replica. The replica host has
// no CORS allow-origin for n71.ai, so every such call failed
// ("blocked by CORS policy: No 'Access-Control-Allow-Origin' header"), and
// because that path runs during session bootstrap it prevented the app from
// authenticating — connected sources and other gated data rendered empty.
//
// It also turned out to be pure downside: `supabaseRead` had ZERO read
// consumers in the app (every data read already uses the primary `supabase`
// client), so the replica client added the auth/CORS failure mode with no
// latency benefit. We therefore point it at the PRIMARY unconditionally. The
// export is kept so any future import resolves to a working client; re-enable a
// real replica only once the replica host serves CORS for the app origin AND
// auth is kept on the primary (never setSession into a replica auth client).
const readClient = baseClient;

export const supabaseRead = readClient;
export const isSupabaseReplicaConfigured = (): boolean => false;
